import { useHotkeys as useLibraryHotkeys } from "react-hotkeys-hook";

// Drop-in for react-hotkeys-hook's useHotkeys that ignores barcode scans.
//
// A barcode scanner types its characters a few ms apart (with Shift for
// capitals), so a scan looks like single-key shortcuts — e.g. an "a" in the
// barcode opened the alternatives panel in billing, a "B" (Shift+B) opened
// bills. Keys this close together are a scan, not a person.
export const SCAN_KEY_GAP_MS = 50;

const MODIFIER_KEYS = ["Shift", "Control", "Alt", "Meta", "CapsLock"];

// Times of the last two non-modifier key presses, and a running count of them
const keyTimes = { last: 0, previous: 0, count: 0 };

if (typeof document !== "undefined") {
  // capture phase, so it runs before the hotkey handlers
  document.addEventListener(
    "keydown",
    (event) => {
      if (MODIFIER_KEYS.includes(event.key)) return;
      keyTimes.previous = keyTimes.last;
      keyTimes.last = event.timeStamp;
      keyTimes.count += 1;
    },
    true
  );
}

// Run a shortcut only when it was a lone key press: not in the middle of a
// scan, and no other key followed right after it. Ctrl / Alt / Meta combos and
// non-character keys (Escape, F-keys, ...) can't come from a scanner, so they
// run straight away as before.
const unlessScanning =
  (callback) =>
    (event, handler) => {
      const couldBeScan =
        event?.key?.length === 1 &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey;
      if (!couldBeScan) {
        callback(event, handler);
        return;
      }
      if (keyTimes.last - keyTimes.previous < SCAN_KEY_GAP_MS) return;
      const countAtPress = keyTimes.count;
      setTimeout(() => {
        if (keyTimes.count === countAtPress) callback(event, handler);
      }, SCAN_KEY_GAP_MS);
    };

export const useHotkeys = (keys, callback, options, dependencies) =>
  useLibraryHotkeys(keys, unlessScanning(callback), options, dependencies);

export default useHotkeys;
